import fromPairs from 'lodash/fromPairs'
import isArray from 'lodash/isArray'
import isDate from 'lodash/isDate'
import isFunction from 'lodash/isFunction'
import isObjectLike from 'lodash/isObjectLike'
import isRegExp from 'lodash/isRegExp'
import isString from 'lodash/isString'
import keys from 'lodash/keys'
import toPairs from 'lodash/toPairs'

export const KLEAR_CLASS = '__klearClass'

export const KLEAR_SCALAR = '__klearScalar'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type KlearClassConstructor = new (...args: any[]) => unknown
export type KlearClassRegistry = Record<string, KlearClassConstructor>

export interface KlearTransformer {
  serialize: (data: unknown) => unknown
  deserialize: (data: unknown) => unknown
}

interface TaggedKlearClassConstructor {
  readonly __klearClass?: string
}

function isPassthroughObject(value: object): boolean {
  return isDate(value) || isRegExp(value) || value instanceof File || value instanceof Blob
}

function getKlearClassTag(value: object): string | null {
  const proto = Reflect.getPrototypeOf(value)
  const ctor = proto?.constructor as TaggedKlearClassConstructor | undefined
  const tag = ctor?.__klearClass
  return isString(tag) && tag.length > 0 ? tag : null
}

function toPlainRecord(value: object): Record<string, unknown> {
  const valueWithJson = value as { toJSON?: () => unknown }
  const plain = isFunction(valueWithJson.toJSON) ? valueWithJson.toJSON() : value

  if (isObjectLike(plain as object) && !isArray(plain)) {
    return plain as Record<string, unknown>
  }

  return { [KLEAR_SCALAR]: plain }
}

function serializeClasses(value: unknown): unknown {
  if (isArray(value)) {
    return value.map(serializeClasses)
  }

  if (!isObjectLike(value as object)) {
    return value
  }

  const obj = value as object
  if (isPassthroughObject(obj)) {
    return obj
  }

  const tag = getKlearClassTag(obj)
  if (!tag) {
    const withIso = obj as { toISOString?: () => string }
    if (isFunction(withIso.toISOString)) {
      return withIso.toISOString()
    }
  }

  const source = tag ? toPlainRecord(obj) : (obj as Record<string, unknown>)
  const out: Record<string, unknown> = {}

  for (const [key, child] of toPairs(source)) {
    out[key] = serializeClasses(child)
  }

  if (tag) {
    out[KLEAR_CLASS] = tag
  }

  return out
}

function deserializeClasses(value: unknown, registry: KlearClassRegistry): unknown {
  if (isArray(value)) {
    return value.map((child) => deserializeClasses(child, registry))
  }

  if (!isObjectLike(value as object)) {
    return value
  }

  const obj = value as object
  if (isPassthroughObject(obj)) {
    return obj
  }

  const record = obj as Record<string, unknown>
  const tag = record[KLEAR_CLASS]

  if (isString(tag)) {
    const ctor = registry[tag]
    if (!ctor) {
      throw new Error(`Unknown ${KLEAR_CLASS} '${tag}'`)
    }

    const rest = { ...record }
    delete rest[KLEAR_CLASS]

    const restKeys = keys(rest)
    if (restKeys.length === 1 && restKeys[0] === KLEAR_SCALAR) {
      return new ctor(deserializeClasses(rest[KLEAR_SCALAR], registry), { validate: false })
    }

    const deserialized = fromPairs(toPairs(rest).map(([key, child]) => [key, deserializeClasses(child, registry)]))
    return new ctor(deserialized, { validate: false })
  }

  const out: Record<string, unknown> = {}
  for (const [key, child] of toPairs(record)) {
    out[key] = deserializeClasses(child, registry)
  }
  return out
}

export function wrapTransformerWithKlearClasses<T extends KlearTransformer>(base: T, registry: KlearClassRegistry): T {
  return {
    serialize: (data: unknown) => base.serialize(serializeClasses(data)),
    deserialize: (data: unknown) => deserializeClasses(base.deserialize(data), registry)
  } as T
}
